import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Address, Customer, DeliveryOptions, Festival, Package, Product } from '@shared';
import { BasketStateModel } from './basket.interfaces';
import { BookableEvent } from './events';
import { generateSlug } from './slug.utils';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly http: HttpClient = inject(HttpClient);

  public fetchFestivalData(id: number): Observable<Festival> {
    return this.http.get<Festival>(
      `${environment.apiBaseUrl}spa/festivals/${id}/lodges-with-days`,
    );
  }

  public fetchFestivals(): Observable<Festival[]> {
    return this.http.get<Festival[]>(`${environment.apiBaseUrl}festivals`);
  }

  /**
   * Loads all bookable events (festivals) from the API and generates slugs from titles
   * The slugs are generated client-side from the festival titles and can be used in URLs
   * to directly access booking steps for a specific event
   *
   * Filters festivals by:
   * - Public festivals only (public === 1 or public === true)
   *
   * @returns Observable of bookable events with apiId, slug, and title
   *
   * @example
   * API Response: { id: 3, title: "Frühlingsfest 2026", public: 1, valid_from_date: "2026-04-18", valid_to_date: "2026-05-10" }
   * Result: { apiId: 3, slug: "fruehlingsfest-2026", title: "Frühlingsfest 2026" }
   */
  public fetchBookableEvents(): Observable<BookableEvent[]> {
    return this.http.get<Festival[]>(`${environment.apiBaseUrl}festivals/default`).pipe(
      map(festivals => {

        const bookableEvents = festivals
          .filter(festival => {
            // Only public festivals (accept both true and 1)
            const isPublic = festival.public === 1 || festival.public === true;
            if (!isPublic) {
              return false;
            }

            return true;
          })
          .map(festival => ({
            apiId: festival.id!,
            slug: generateSlug(festival.title),
            title: festival.title,
            valid_from_date: festival.valid_from_date,
            valid_to_date: festival.valid_to_date
          }));

        return bookableEvents;
      })
    );
  }

  public submitBookingRequest(booking: BasketStateModel) {
    return this.http.post<void>(
      `${environment.apiBaseUrl}spa/reservation`,
      booking,
    );
  }
}
